"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useStickyHeader = void 0;
const ios_inner_height_1 = __importDefault(require("ios-inner-height"));
const lodash_1 = require("lodash");
const react_1 = __importDefault(require("react"));
/**
 * A hook to aid in providing sticky behaviour to elements.
 *
 * The hook returns two React refs (generically typed for any HTML element),
 * one for the containing component and another for the component to "stick".
 *
 * It adds/removes classnames to the referenced elements to
 * indicate what the state should be. You will need to write your own CSS to do the sticking.
 *
 * `wrapperRef`:
 * - When scrolling down the page, `sticky` classname will be attached
 * - When scrolling up the page or resting at the top, `sticky` will be replaced with `default`
 *
 * `stickyRef`:
 * - When scrolling down the page, `direction-bottom` will be attached.
 * - When scrolling up the page, `direction-bottom` will be removed.
 *
 * @returns { wrapperRef, stickyRef }:
 *
 *
 */
function useStickyHeader() {
    const wrapperRef = react_1.default.useRef(null);
    const stickyRef = react_1.default.useRef(null);
    const prevScrollPosition = react_1.default.useRef(window.scrollY);
    react_1.default.useEffect(() => {
        const handleScroll = (0, lodash_1.throttle)(() => {
            const stickyWrapper = wrapperRef.current;
            const stickyComponent = stickyRef.current;
            if (!(stickyWrapper || stickyComponent))
                return;
            if (window.scrollY <= 0) {
                console.debug('Not sticky');
                stickyWrapper === null || stickyWrapper === void 0 ? void 0 : stickyWrapper.classList.add('default');
                stickyWrapper === null || stickyWrapper === void 0 ? void 0 : stickyWrapper.classList.remove('sticky');
            }
            // check if scroll position > 0
            if (window.scrollY > 0) {
                console.debug('Sticky');
                stickyWrapper === null || stickyWrapper === void 0 ? void 0 : stickyWrapper.classList.add('sticky');
                stickyWrapper === null || stickyWrapper === void 0 ? void 0 : stickyWrapper.classList.remove('default');
            }
            // https://stackoverflow.com/a/17698713
            const maxScroll = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight, document.body.offsetHeight, document.documentElement.offsetHeight, document.body.clientHeight) - (0, ios_inner_height_1.default)();
            // Safari overscrolling allows you to reach negative scrollY values;
            const reachedMinScroll = window.scrollY <= 0;
            const reachedMaxScroll = window.scrollY >= maxScroll;
            const withinRegularBoundary = !reachedMaxScroll && !reachedMinScroll;
            if (withinRegularBoundary) {
                if ((Math.sign(window.scrollY - prevScrollPosition.current) === 1)) {
                    console.debug('Scrolling down');
                    stickyComponent === null || stickyComponent === void 0 ? void 0 : stickyComponent.classList.add('direction-bottom');
                }
                if ((Math.sign(window.scrollY - prevScrollPosition.current) === -1)) {
                    console.debug('Scrolling up');
                    stickyComponent === null || stickyComponent === void 0 ? void 0 : stickyComponent.classList.remove('direction-bottom');
                }
            }
            // update ref to store up to date scroll position
            prevScrollPosition.current = window.scrollY;
        }, 100, { leading: true });
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    return { wrapperRef, stickyRef };
}
exports.useStickyHeader = useStickyHeader;
//# sourceMappingURL=useStickyHeader.js.map